/**
 * MediaLightbox — full-screen viewer with a Reels / Shorts-style
 * VERTICAL snap-scroll experience.
 *
 * Interaction:
 *  • Swipe UP / DOWN on touch, mouse-wheel, or trackpad scrolls between slides.
 *  • Keyboard: ↑ / ↓ (or PageUp / PageDown / Space) to navigate, Esc to close.
 *  • Each slide fills the viewport (100dvh) with CSS scroll-snap.
 *  • The active slide's video/iframe auto-plays (muted for autoplay policy);
 *    off-screen media are paused/unmounted to save memory.
 *
 * Item shape (unchanged for backwards compatibility):
 *    { kind: "image",  url, caption?, alt? }
 *    { kind: "iframe", src, caption?, aspect? }     // YouTube / IG / TikTok embed
 *    { kind: "video",  src, caption? }              // direct .mp4 / .webm
 *
 * Portal'd to document.body so it always stacks above the page.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X as CloseIcon } from "lucide-react";

// Append autoplay/mute query params to an embed URL so the active slide
// starts playing automatically (mobile browsers require muted autoplay).
function withAutoplay(src) {
  if (!src) return src;
  try {
    const u = new URL(src, window.location.origin);
    u.searchParams.set("autoplay", "1");
    u.searchParams.set("mute", "1");
    u.searchParams.set("muted", "1");
    u.searchParams.set("playsinline", "1");
    return u.toString();
  } catch {
    // Non-standard URL — fall back to a plain query-string join.
    const sep = src.includes("?") ? "&" : "?";
    return `${src}${sep}autoplay=1&mute=1&muted=1&playsinline=1`;
  }
}

export default function MediaLightbox({ items, startIndex = 0, onClose, testid = "media-lightbox" }) {
  const safeStart = Math.max(0, Math.min(startIndex, (items?.length || 1) - 1));
  const [i, setI] = useState(safeStart);
  const containerRef = useRef(null);
  const slideRefs = useRef([]);
  const videoRefs = useRef({});
  const total = items?.length || 0;

  // Programmatic scroll to a given index.
  const scrollToIdx = (idx, behavior = "smooth") => {
    const el = slideRefs.current[idx];
    if (!el || !containerRef.current) return;
    containerRef.current.scrollTo({ top: el.offsetTop, behavior });
  };

  // Body scroll lock + keyboard nav.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        onClose?.();
      } else if (e.key === "ArrowDown" || e.key === "PageDown" || e.key === " ") {
        e.preventDefault();
        scrollToIdx(Math.min(i + 1, total - 1));
      } else if (e.key === "ArrowUp" || e.key === "PageUp") {
        e.preventDefault();
        scrollToIdx(Math.max(i - 1, 0));
      }
    };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [i, total, onClose]);

  // Jump to the requested start slide once refs are attached (no smooth
  // scroll on open so the correct reel is instantly on-screen).
  useEffect(() => {
    scrollToIdx(safeStart, "auto");
    setI(safeStart);
  }, []);

  // Track which slide is currently the active one via IntersectionObserver.
  useEffect(() => {
    if (!containerRef.current) return;
    const obs = new IntersectionObserver(
      (entries) => {
        // Pick the entry with the highest visible ratio.
        let best = null;
        for (const e of entries) {
          if (!best || e.intersectionRatio > best.intersectionRatio) best = e;
        }
        if (best && best.isIntersecting && best.intersectionRatio >= 0.6) {
          const idx = Number(best.target.dataset.idx);
          if (!Number.isNaN(idx)) setI(idx);
        }
      },
      { root: containerRef.current, threshold: [0.6, 0.9] }
    );
    slideRefs.current.forEach((el) => el && obs.observe(el));
    return () => obs.disconnect();
  }, [total]);

  // Play the active <video>, pause everything else.
  useEffect(() => {
    Object.entries(videoRefs.current).forEach(([idx, el]) => {
      if (!el) return;
      if (Number(idx) === i) {
        // Muted autoplay is allowed by browsers; unmute-on-tap is up to the user.
        el.muted = true;
        el.play?.().catch(() => {});
      } else {
        el.pause?.();
        try { el.currentTime = 0; } catch { /* no-op */ }
      }
    });
  }, [i]);

  // Precompute which slides should render heavy media (active ± 1 window).
  const renderWindow = useMemo(() => new Set([i - 1, i, i + 1]), [i]);

  if (!items || items.length === 0) return null;

  const body = (
    <div
      className="fixed inset-0 bg-black z-[80] select-none"
      data-testid={testid}
    >
      {/* Close button — floats above the scroll container */}
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        data-testid={`${testid}-close`}
        className="fixed top-4 right-4 z-[90] border-2 border-cream bg-black/60 text-cream p-2 hover:bg-cream/20"
      >
        <CloseIcon className="w-5 h-5" />
      </button>

      {/* Slide counter — top center */}
      {total > 1 && (
        <div
          className="fixed top-4 left-1/2 -translate-x-1/2 z-[90] text-cream text-xs font-black uppercase tracking-widest bg-black/60 border-2 border-cream px-3 py-1"
          data-testid={`${testid}-counter`}
        >
          {i + 1} / {total}
        </div>
      )}

      {/* Vertical snap-scroll rail */}
      <div
        ref={containerRef}
        className="h-[100dvh] w-full overflow-y-auto overflow-x-hidden snap-y snap-mandatory overscroll-contain"
        data-testid={`${testid}-rail`}
        style={{ scrollbarWidth: "none" }}
      >
        {items.map((it, idx) => {
          const inWindow = renderWindow.has(idx);
          return (
            <section
              key={idx}
              ref={(el) => (slideRefs.current[idx] = el)}
              data-idx={idx}
              data-testid={`${testid}-slide-${idx}`}
              className="h-[100dvh] w-full snap-start snap-always flex items-center justify-center relative"
            >
              {/* Media */}
              <div className="w-full h-full flex items-center justify-center px-4">
                {it.kind === "image" && (
                  <img
                    src={it.url}
                    alt={it.alt || it.caption || ""}
                    className="max-w-full max-h-[90dvh] object-contain"
                    data-testid={`${testid}-image-${idx}`}
                    loading={inWindow ? "eager" : "lazy"}
                  />
                )}

                {it.kind === "iframe" && (
                  <div
                    className="border-2 border-cream bg-black"
                    style={{
                      aspectRatio: it.aspect === "portrait" ? "9 / 16" : "16 / 9",
                      width: it.aspect === "portrait" ? "min(420px, 92vw)" : "min(900px, 96vw)",
                      maxHeight: "88dvh",
                    }}
                    data-testid={`${testid}-iframe-wrap-${idx}`}
                  >
                    {inWindow ? (
                      <iframe
                        // Re-key when this becomes the active slide so YouTube
                        // (re)starts playback on entry.
                        key={idx === i ? `${idx}-active` : `${idx}-idle`}
                        src={idx === i ? withAutoplay(it.src) : it.src}
                        title={it.caption || `Video ${idx + 1}`}
                        className="w-full h-full"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        allowFullScreen
                        data-testid={`${testid}-iframe-${idx}`}
                      />
                    ) : null}
                  </div>
                )}

                {it.kind === "video" && (
                  <video
                    ref={(el) => {
                      if (el) videoRefs.current[idx] = el;
                      else delete videoRefs.current[idx];
                    }}
                    src={inWindow ? it.src : undefined}
                    controls
                    playsInline
                    loop
                    muted
                    preload={inWindow ? "auto" : "none"}
                    className="max-w-full max-h-[90dvh] bg-black border-2 border-cream"
                    data-testid={`${testid}-video-${idx}`}
                  />
                )}
              </div>

              {/* Caption overlay — Reels-style bottom gradient */}
              {it.caption && (
                <div
                  className="pointer-events-none absolute inset-x-0 bottom-0 pb-8 pt-14 px-6 bg-gradient-to-t from-black/85 via-black/45 to-transparent"
                  data-testid={`${testid}-caption-${idx}`}
                >
                  <p className="text-cream text-sm font-bold text-center max-w-2xl mx-auto">
                    {it.caption}
                  </p>
                </div>
              )}
            </section>
          );
        })}
      </div>

      {/* Hide scrollbar (WebKit) */}
      <style>{`
        [data-testid="${testid}-rail"]::-webkit-scrollbar { display: none; }
      `}</style>
    </div>
  );

  return typeof document !== "undefined" ? createPortal(body, document.body) : body;
}
